import React, { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { useForm } from 'react-hook-form';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import { IDocumentTable } from '@models/Forms/IForms';
import { IFieldElem, IFormData } from '@models/IFormData';
import { IBatchEditingTableData } from '@models/actions/IBatchEditingTableData';
import { IDocumentResult } from '@models/document/IDocumentResult';
import { ModalSize } from '@srm/ui/types/ModalSize';
import EditTable from '@molecules/formbuilder/controls/Table/controls/EditTable';
import { sendModalMsg } from '@srm/ui/lib/modalMessages';
import { convertListToMap } from '@utils/documentUtils';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

/** Источник таблицы: вся разница между действием активити и действием документа. */
export interface IBatchEditingTableSource {
    getScheme: () => Promise<AxiosResponse<IDocumentTable>>;
    getData: () => Promise<AxiosResponse<IBatchEditingTableData>>;
    save: (values: IFormData) => Promise<AxiosResponse<IDocumentResult>>;
}

export interface IBatchEditingTableModalProps {
    source: IBatchEditingTableSource;
    /** Класс обёртки содержимого — на него завязаны стили конфигураций. */
    contentClassName: string;
    title: string;
    okButtonText: string;
    cancelButtonText: string;
    modalSize: ModalSize;
    completeHandler?: (isSucceed: boolean) => void;
}

/** Пакетное редактирование таблицы в модалке: схема и данные приходят из source,
 *  строки в EditTable нельзя добавлять, удалять и копировать. */
export const BatchEditingTableModal: React.FC<IBatchEditingTableModalProps> = (props) => {
    const [errorText, setErrorText] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [tableScheme, setTableScheme] = useState<IDocumentTable>();
    const [fields, setFields] = useState<Record<string, IFieldElem>>({});

    const formMethods = useForm<any>({
        mode: 'onBlur',
    });
    const hasUnsavedInput = useUnsavedFormInput(formMethods);

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setLoading(true);

        Promise.all([props.source.getScheme(), props.source.getData()])
            .then((values) => {
                if (ignore) return;
                const [scheme, data] = values;
                const tableScheme = scheme.data;
                const baseDocData = data.data.baseDocData;
                const tableData = data.data.tableData;

                const mergedFields = [...tableData.fields, ...baseDocData.fields];
                const mergedFieldsMap = convertListToMap(mergedFields);

                tableScheme.addRowButtonRules = 'false';
                tableScheme.singleRowDeleteButtonRules = 'false';
                tableScheme.removeRowButtonRules = 'false';
                tableScheme.singleRowCopyButtonRules = 'false';
                tableScheme.copyRowButtonRules = 'false';

                formMethods.reset({ ...tableData, fields: mergedFields });
                setTableScheme(tableScheme);
                setFields(mergedFieldsMap);
            })
            .catch(() => {
                if (!ignore) setErrorText('Ошибка получения данных от сервера.');
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, []);

    const onSubmit = () => {
        setSaving(true);
        const formValues = formMethods.getValues();

        return props.source
            .save(formValues)
            .then(() => {
                props.completeHandler && props.completeHandler(true);
            })
            .catch((error) => setErrorText(error))
            .finally(() => {
                setSaving(false);
            });
    };

    const modalContainerProps: IModalContainerProps = {
        header: props.title,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: loading || saving,
        closeDisabled: saving,
        hasUnsavedInput,
        onCancel: () => {
            props.completeHandler && props.completeHandler(false);
        },
        errorText: errorText,
        formMethods: formMethods,
        onSubmit: onSubmit,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <div className={props.contentClassName}>
                {tableScheme && fields && (
                    <EditTable
                        table={tableScheme}
                        formMethods={formMethods}
                        setError={(errors?: string[]) => {
                            if (errors) {
                                sendModalMsg({
                                    message: errors,
                                    type: 'error',
                                });
                            }
                        }}
                        isEdit={true}
                        isNew={false}
                        turnOffInvokeChangesOnNew={false}
                        fields={fields}
                    />
                )}
            </div>
        </ModalContainer>
    );
};
